/**
 * Admin API Service
 * API functions copied from admin project and adapted for fx-admin
 * Uses localStorage-based authentication instead of cookies
 */

import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from './apiClient';
import { getAuthToken, setAuthToken } from './apiConfig';

const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

/**
 * Admin Sign In
 * @param {Object} data - { email: string, password: string }
 * @returns {Promise}
 */
export const signIn = async (data) => {
  try {
    const response = await apiPost('/api/admin/login', data, { skipAuth: true });
    
    // Store token if provided in response
    if (response.token) {
      setAuthToken(response.token);
    } else if (response.data?.token) {
      setAuthToken(response.data.token);
    }
    
    return response;
  } catch (error) {
    console.error("Error occurred during signIn API call:", error);
    throw error;
  }
};

/**
 * GET API request
 * @param {string} url - API endpoint (without /api/admin/ prefix, it will be added automatically)
 * @returns {Promise}
 */
export const getApi = async (url) => {
  try {
    // Ensure url starts with /api/admin/ if it doesn't already
    let endpoint = url;
    if (!url.startsWith('/api/admin/')) {
      if (url.startsWith('/admin/')) {
        endpoint = `/api${url}`;
      } else {
        endpoint = `/api/admin/${url}`;
      }
    }
    const response = await apiGet(endpoint);
    return response;
  } catch (error) {
    console.error("Error occurred during API call:", error);
    throw error;
  }
};

/**
 * POST API request
 * @param {string} url - API endpoint (without /api/admin/ prefix, it will be added automatically)
 * @param {Object|FormData} data - Request body data
 * @param {Object} options - Additional options (useFormData, etc.)
 * @returns {Promise}
 */
export const postApi = async (url, data, options = {}) => {
  try {
    // Ensure url starts with /api/admin/ if it doesn't already
    let endpoint = url;
    if (!url.startsWith('/api/admin/')) {
      if (url.startsWith('/admin/')) {
        endpoint = `/api${url}`;
      } else {
        endpoint = `/api/admin/${url}`;
      }
    }
    const requestOptions = data instanceof FormData || options.useFormData 
      ? { ...options, useFormData: true }
      : options;
    const response = await apiPost(endpoint, data, requestOptions);
    return response;
  } catch (error) {
    console.error("Error occurred during POST API call:", error);
    throw error;
  }
};

/**
 * UPDATE API request (PUT)
 * @param {string} url - API endpoint (without /api/admin/ prefix, it will be added automatically)
 * @param {Object} data - Request body data
 * @returns {Promise}
 */
export const updateApi = async (url, data) => {
  try {
    // Ensure url starts with /api/admin/ if it doesn't already
    let endpoint = url;
    if (!url.startsWith('/api/admin/')) {
      if (url.startsWith('/admin/')) {
        endpoint = `/api${url}`;
      } else {
        endpoint = `/api/admin/${url}`;
      }
    }
    const response = await apiPut(endpoint, data);
    return response;
  } catch (error) {
    console.error("Error occurred during UPDATE API call:", error);
    throw error;
  }
};

/**
 * DELETE API request
 * @param {string} url - API endpoint (without /api/admin/ prefix, it will be added automatically)
 * @returns {Promise}
 */
export const deleteApi = async (url) => {
  try {
    // Ensure url starts with /api/admin/ if it doesn't already
    let endpoint = url;
    if (!url.startsWith('/api/admin/')) {
      if (url.startsWith('/admin/')) {
        endpoint = `/api${url}`;
      } else {
        endpoint = `/api/admin/${url}`;
      }
    }
    const response = await apiDelete(endpoint);
    return response;
  } catch (error) {
    console.error("Error occurred during DELETE API call:", error);
    throw error;
  }
};

/**
 * PATCH API request
 * @param {string} url - API endpoint (without /api/admin/ prefix, it will be added automatically)
 * @param {Object} data - Request body data
 * @returns {Promise}
 */
export const patchApi = async (url, data) => {
  try {
    // Ensure url starts with /api/admin/ if it doesn't already
    let endpoint = url;
    if (!url.startsWith('/api/admin/')) {
      if (url.startsWith('/admin/')) {
        endpoint = `/api${url}`;
      } else {
        endpoint = `/api/admin/${url}`;
      }
    }
    const response = await apiPatch(endpoint, data);
    return response;
  } catch (error) {
    console.error("Error occurred during PATCH API call:", error);
    throw error;
  }
};

/**
 * PUT API request (alias for updateApi, for compatibility)
 * @param {string} url - API endpoint (without /api/admin/ prefix, it will be added automatically)
 * @param {Object} data - Request body data
 * @returns {Promise}
 */
export const putApi = async (url, data) => {
  return updateApi(url, data);
};

